// The ScriptSystem (SCR-6, ARCH §27.4). PURE (ENG-1) — ONE script runtime, and it
// lives inside the sim.
//
// Until now `src/client/boot.ts` owned the compile cache, the redstone rising-edge
// latch and the dispatcher, and `server/` referenced this module zero times — so a
// `script_block` on a dedicated server did not run at all. This file closes that gap
// (a bug fix, not a refactor of taste): the client and the server construct the same
// `createScriptSystem()` and call it from the same `blockEntities` tick phase, so a
// script behaves identically solo and server-authoritative.
//
// PURITY: nothing here touches `three`, the DOM, or `client/`. Everything the runtime
// cannot know by itself arrives through its inputs — the verb surface as `ScriptHost`
// (`api.ts`), the cells to sweep and the redstone power probe as `ScriptTickCtx`. That
// is also why the AVI-3 autopilot preset a client-side script block may carry is NOT
// handled here: it is client state, so the host claims that rising edge instead
// (`ScriptTickCtx.claim`).
//
// SAFETY (E-30, "abort not throw", cf. E-23): `dispatch` RETURNS an abort and never
// throws. `install` funnels through `tryCompile`, which never throws either, so a
// broken source is a refusal the editor can show rather than a crash. Nothing on any
// path below can reach the tick loop as an exception.
//
// DETERMINISM (ENG-2/3): the runtime holds only a compile cache keyed by the host's
// key, a rising-edge latch, and per-key counters. It reads no clock and no RNG; the
// sweep order is the host's iteration order over its cells, so solo, server and CI
// dispatch in the same order.
//
// MOB HOOKS (M45.3, §27.6) arrive as `dispatchMob` — the `entities`-phase entry §27.4
// places **before** each entity's archetype. Three things make it work, and all three
// were missing:
//
//   1. `install` now passes the HOST KIND to `tryCompile`, so a mob source is compiled
//      against §27.5's mob slice. Without it the slice was enforced only where a caller
//      remembered to pass `'mob'` to `compile` by hand — i.e. in tests, not in the game.
//   2. A `ScriptBinding` may now carry the MOB SURFACE (`ScriptMobHost`), which `run`
//      forwards into the dispatch's `VerbCtx` (see `vm.ts`).
//   3. `MobScriptSources` RESOLVES a `MobDef.script` NAME to source text. M45.1 added
//      the field and `data/scripts/` but left the resolver unowned, so the name pointed
//      at nothing. It is a lookup the HOST supplies, because reading `data/scripts/*.tfs`
//      from disk (Node) or a bundle (browser) is host I/O and `sim/` is pure (ENG-1).
//      Keeping the table on the system is also what makes M45.6's hot reload a
//      one-liner: `install` recompiles on source change, so updating the table swaps
//      the program for every live entity of that type while `scriptMem` survives.
//
// THE MOB LADDER (M45.5, §27.8) is the schedule every mob dispatch now passes through,
// and it is deliberately ALL in this file — the entity layer hands over four facts
// (which entity, which type, which tick, which rate) and the runtime decides the rest,
// because every counter the ladder needs is runtime state:
//
//   • BUDGET — `budgetFor` finally distinguishes the host kinds. A mob is measured
//     against `SCR.MOB.OP_BUDGET`, and against the SAME number twice: §27.3's static
//     bound at `install` and the VM's ceiling at `dispatch`. The number is computed
//     once and stored on the cache entry, so the two cannot drift — which is exactly
//     what D-TF-171's "clearing the save gate guarantees the dispatch fits" rests on.
//   • CADENCE — a `tick` hook runs only when `entityId % divisor === tick % divisor`
//     (5 Hz at 20 TPS). Event hooks (`hurt`/`death`/`interact`/…) are NOT staggered:
//     they fire because something happened to that mob, and there is no later tick in
//     which a dead mob can still run its `on death`.
//   • FRAME CEILING — `SCR.MOB.FRAME_CEILING` ops per tick across every scripted
//     entity, consumed in the entity-id order the entity system already ticks in, so
//     the deferred tail is a pure function of sim state rather than a race.
//   • QUARANTINE — `SCR.MOB.QUARANTINE_K` consecutive aborts quarantine the TYPE (not
//     the entity), with exactly ONE chat line, emitted at the latch. In memory only:
//     an edit clears it (`install` resets the ladder whenever it replaces a program)
//     and a save→load clears it because nothing here is ever serialized.
//   • RNG — `scriptRng(entityScriptSeed(mobSeed, entityId), tick)`, re-derived every
//     dispatch and never stored (§27.8), which is what makes `random`/`chance` work in
//     a mob script at all.
//
// THE TICK SOURCE is injected, not read (ARCH §27.19 names "an injected tick source"
// as the seam for stagger/ceiling tests). `sim/` has one canonical monotonic tick —
// `World.tickCount`, which `engine/tick.ts` increments once per `runTick` — but the
// entity layer sees a `MobWorld`, not a `World`, so the host passes it down as
// `MobFrame.tick`. It is OPTIONAL, and its absence is one condition with three honest
// consequences: no tick ⇒ no cadence, no frame ceiling (a ceiling with no frame
// boundary would never reset) and an rng that cannot fork per dispatch. Wiring the
// tick source turns all three on together.
//
// HOT RELOAD AND DIAGNOSTICS (M45.6, §27.16) are the last two rungs, and both are
// mostly consequences of what the ladder already built:
//
//   • RELOAD is `install` plus ONE rule. `dispatchMob` re-resolves a script NAME every
//     dispatch, so changing what the host's `MobScriptSources` table returns swaps the
//     program for every live entity of that type on its next dispatch — and `scriptMem`
//     survives because it lives on the ENTITY (§27.7), not on the program: there is no
//     code path here that could drop it, which is the strongest form of "preserved".
//     The one rule is E-44: a reload whose source is REFUSED keeps the LAST-GOOD
//     program running and surfaces the refusal, instead of bricking every sheep in the
//     world (the `reskinMob` pattern). So `Entry.source` is what was last OFFERED and
//     `Entry.program` is what is RUNNING, and the two diverge exactly in that state —
//     which `diagnostics()` reports as `compileError` set WHILE `installed` is true.
//     The quarantine ladder is cleared only when a program was actually SWAPPED,
//     because a refused edit changed nothing to forgive.
//   • DIAGNOSTICS are the per-key ERROR RING (`SCR.ERROR_RING` entries, oldest evicted
//     first) and the LAST-DISPATCH EFFECTS list. The ring takes compile refusals at
//     `install` — once each, because `install` is idempotent per source, so a broken
//     script dispatching at 5 Hz cannot flood its own history — and runtime aborts at
//     `dispatch`. It does NOT take a `ScriptDeferred`: a mob that was off-beat, ceiling-
//     deferred or quarantined did not FAIL, and at 5 Hz those would outnumber real
//     aborts three to one. The ring SURVIVES a reload, because an author's last sixteen
//     errors are the reason they are editing. The effects list is recorded at the one
//     seam that sees every effect — the surfaces this file hands the VM — by wrapping
//     `ScriptHost`/`ScriptMobHost` in an `EffectRecorder`, so it reports what the host
//     was actually asked to do rather than what the source looks like it does.
//
// BOTH ARE DETERMINISTIC (ENG-2), and deliberately so: each is an ARRAY appended in
// dispatch order and evicted from the front, every field in it (`tick`, `line`,
// `message`, the verb and its arguments) is a pure function of the dispatch, and no
// `Map`/`Set` iteration order reaches either one. Nothing in the sim ever READS them
// back either — they leave only through `diagnostics()`, to an editor — so M45.7's
// 10 000-tick hash cannot see them at all. That is what lets the observability surface
// grow later without re-opening the determinism contract.
//
// Deliberately NOT here yet: §27.6's `event.*` payloads beyond `name` (they need
// `EventView` + `NAMESPACE_PROPS` widened, which is M46's, `api.ts` being another
// task's file); §27.16's CLIPPED COUNTS, which count what §27.10's cascade/emit/spawn
// limits drop and so cannot exist before the thing that drops them (M46); the block
// lifecycle bus and the cascade queue (M46, §27.9/§27.10); and the `say` rate limits
// of §27.8's last bullet, which belong with the chat surface rather than with the
// schedule. The interface below is §27.4's, still.
//
// TRUST TIERS (M47.1, §27.12) enter at `install` and nowhere else: the tier gates the
// program's COMPUTED capability set (`tiers.ts`) and picks its budget, and both land on
// the cache entry like every other limit. `local` is the default and the pre-M47 path,
// so every existing call site — and every existing golden — is unchanged.
import { ScriptError } from './lang/lex';
import { SCR, TICK } from '../config';
import { tryCompile, type CompileResult, type Program } from './lang/parse';
import { programCost, overBudget, overBudgetError } from './lang/cost';
import { programCapabilities } from './capabilities';
import { checkTier, sameTrust, LOCAL_TRUST, SHIPPED_TIER_POLICY, type ScriptTrust, type TierPolicy } from './tiers';
import { run, type ScriptResult, type SelfView, type EventView } from './lang/vm';
import { vec3, valueToString, type Value } from './lang/values';
import { entityScriptSeed, scriptRng } from './rng';
import type { MobSteering, ScriptHost, ScriptMobHost } from './api';
import type { ScriptRng } from './rng';

/** Which slice of the verb table a source is compiled for (§27.5). Both kinds are live
 *  since M45.3: a block cell installs `'block'` from the sweep below, a `MobDef.script`
 *  installs `'mob'` from `dispatchMob`, and the kind reaches `tryCompile` either way. */
export type ScriptHostKind = 'block' | 'mob';

/** The outcome of an `install`. A blank source is NOT an error — it installs nothing
 *  and reports `empty`, exactly as an empty script block behaved before the move. */
export type InstallResult =
  | { ok: true; empty: boolean }
  | { ok: false; error: ScriptError };

/** The dispatched event — today exactly the VM's `event.*` view (`name` selects the
 *  `on <name>` handler, `power` is the redstone level that triggered it). M46 widens
 *  this view (`amount`/`source`/`payload`) in `vm.ts`, and this alias follows it. */
export type ScriptEvent = EventView;

/** Everything one dispatch runs against. The runtime supplies the program; the host
 *  supplies the effect surface and the `self.*` view. */
export interface ScriptBinding {
  /** The whitelisted verb surface (SCR-2) — the ONLY way a script reaches the world. */
  host: ScriptHost;
  /** `self.*` for this dispatch (the block's own cell + whether it is powered). */
  self: SelfView;
  /** The MOB this dispatch is for (§27.5's mob slice). Absent ⇒ a block dispatch, so
   *  `self.*`/`mem.*`/`handled()` abort and the block-only verbs stay legal. */
  mob?: ScriptMobHost;
  /** The per-dispatch seeded rng behind `random`/`chance` (§27.8), derived by the
   *  caller and never stored. Absent ⇒ those two verbs abort rather than fall back to
   *  an unseeded source. `dispatchMob` derives it for every mob dispatch; a block
   *  dispatch has no entity to fork on, so the block sweep still leaves it empty. */
  rng?: ScriptRng;
  /** The sim tick this dispatch belongs to, for §27.10's per-tick spawn budget. Absent
   *  ⇒ the budget still applies, but it rolls over on `tick()` rather than on the tick
   *  number — see `rollSpawnTick`. */
  tick?: number;
  /** The `on <name>` block to run, when it is NOT `event.name` — see `RunOptions.handler`.
   *  Only `on signal` uses it (D-TF-217). */
  handler?: string;
}

/** §27.8's `MobDef.scriptHz` — how often a scripted mob's `on tick` hook dispatches.
 *  The default is `TICK.TPS / SCR.MOB.TICK_DIVISOR` (5 Hz at 20 TPS) and is expressed
 *  by leaving the field off; `20` is the opt-in that dispatches every tick and pays
 *  for it with a quartered per-dispatch budget. Structurally identical to
 *  `MobDef.scriptHz` in `registry/schemas.ts`, and deliberately not imported from
 *  there: the registry describes content, and the envelope belongs to the runtime that
 *  enforces it. `tests/script_budget.test.ts` pins the two shapes together. */
export type MobScriptHz = 5 | 20;

/** A dispatch §27.8's SCHEDULE declined to run: an off-beat cadence tick, a frame-
 *  ceiling deferral, or a quarantined type. It is NOT a script failure — the mob ran
 *  its archetype this tick, exactly as an unscripted one does — and the distinct class
 *  is what lets M45.6's error ring keep these out of a script's error list, where at
 *  5 Hz they would outnumber real aborts three to one. */
export class ScriptDeferred extends ScriptError {}

/** A mob dispatch found NO SOURCE for its script name (§27.6's resolver returned
 *  nothing). Not a script failure and not a rung on the quarantine ladder — a mob with no
 *  script yet — but the one refusal a caller holding a FALLBACK must tell apart from the
 *  rest: §27.13's per-instance override falls back to the type's script on exactly this
 *  (E-50), and on nothing else. */
export class ScriptMissing extends ScriptError {}

/** §27.6's six mob hooks, in the order that table lists them. A hook name is what
 *  `event.name` carries, so `on tick { … }` runs for `'tick'` and nothing else. */
export type MobHook = 'tick' | 'hurt' | 'target' | 'death' | 'interact' | 'signal';

/** §27.9's four block lifecycle hooks, joining the `on redstone` the sweep already
 *  fires. Like `MobHook`, the string IS what `event.name` carries.
 *
 *  §27.9 names the four hooks but documents no `event.*` payload table for them the way
 *  §27.6 does for mobs, so the fields below are this build's minimum reading and are
 *  logged as D-TF-208:
 *
 *  | Hook          | `event.*`        | why |
 *  |---------------|------------------|-----|
 *  | `placed`      | `name`           | the cell is `self`; there is nothing else to say |
 *  | `broken`      | `name`           | likewise — the block is going away, not changing |
 *  | `neighbor`    | `name`, `source` | `source` = the vec3 of the face-adjacent cell that changed; without it a handler cannot tell a change BELOW it from one BESIDE it, which is the whole use for the hook |
 *  | `random_tick` | `name`           | it is a clock, and carries no cause |
 */
export type BlockHook = 'placed' | 'broken' | 'neighbor' | 'random_tick';

/** One queued block lifecycle dispatch, waiting for the cascade drain (§27.10).
 *
 *  It carries the SOURCE TEXT as well as the cell because a lifecycle dispatch, unlike
 *  the redstone sweep, is not driven by iterating live cells: it is queued when
 *  something happens and run a phase later, by which time the block entity may be gone
 *  (`on broken`, necessarily). Capturing the source at enqueue is what lets `on broken`
 *  run at all. */
export interface LifecycleDispatch {
  /** Cache + latch key for the cell, exactly as `ScriptCell.key` shapes it. */
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The cell's script source, captured at enqueue (see above). */
  readonly source: string;
  readonly hook: BlockHook;
  /** §27.10's cascade depth. A world-caused event enqueues at 0; a script-caused edit
   *  enqueues its consequences at the causing dispatch's depth + 1, and past
   *  `SCR.CASCADE.MAX_DEPTH` the enqueue is refused and counted. */
  readonly depth: number;
  /** For `'neighbor'`: the face-adjacent cell that changed, surfaced as `event.source`. */
  readonly from?: { readonly x: number; readonly y: number; readonly z: number };
  /** The key of the script whose dispatch CAUSED this enqueue, when one did. A clip is
   *  attributed to the causer rather than the target: the author who needs to see
   *  "your fill minted more reactions than the budget allows" is the one who wrote the
   *  fill, not whoever owns the 4096 blocks it touched. */
  readonly cause?: string;
}

/** What §27.10's bounds dropped, for §27.16's "clipped counts" read-out. Counts are
 *  per-script and cumulative until `resetClipped`, because a cascade that clips once is
 *  usually clipping every tick and a single-tick number would read as noise. */
export interface ClippedCounts {
  /** Lifecycle/signal dispatches refused by `MAX_DISPATCH` or `MAX_DEPTH`. */
  readonly dispatches: number;
  /** `emit`s refused by depth, radius dedupe or the dispatch cap (M46.3). */
  readonly emits: number;
  /** `summon`s refused by `SPAWN_BUDGET` or the MOB-4 caps (M46.4). */
  readonly spawns: number;
}

/** Zero of everything — the state a script that has never been clipped reports. */
export const NO_CLIPS: ClippedCounts = { dispatches: 0, emits: 0, spawns: 0 };

/** One raised signal waiting for delivery (§27.10). Queued by `emit`, delivered at the
 *  next drain to the scripted entities within `SCR.SIGNAL.RADIUS` of where it was
 *  raised — so an emitter never names a recipient and cannot call one. */
export interface PendingSignal {
  /** The signal name; selects the receiver's `on signal` handler via `event.name`. */
  readonly name: string;
  /** §27.10's single value — "not a list, not a bag" (D-TF-105). */
  readonly payload: Value;
  /** Where it was raised; delivery is by distance from here. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The emitting dispatch's depth. Delivery happens at `depth + 1` (§27.10). */
  readonly depth: number;
  /** The key of the script that emitted, for clip attribution. */
  readonly cause?: string;
}

/** A scripted entity a signal may reach. The host resolves who is in range — the
 *  runtime has no entity index and must not grow one (it would be a second, drifting
 *  copy of the one `EntitySystem` already owns). */
export interface SignalTarget {
  /** §27.8's ordering key. §27.10: "Delivery order is entity-id then depth", so this
   *  is what makes a clipped cascade clip identically on every machine. */
  readonly entityId: number;
  /** The `MobDef.script` name this entity runs. */
  readonly script: string;
  /** This entity's `self.*`/`mem.*` surface for the delivered dispatch. */
  readonly mob: ScriptMobHost;
  /** `Entity.type`, for §27.8's per-type quarantine ladder. */
  readonly type?: string;
  /** `MobDef.scriptHz`, for the cadence tier's budget. */
  readonly hz?: MobScriptHz;
}

/** Resolves a `MobDef.script` NAME to source text (§27.6: "a script **name** resolving
 *  to `data/scripts/<name>.tfs` **or an overlay-provided source**").
 *
 *  The sim cannot read a file (ENG-1), so this is a lookup the HOST fills — from the
 *  data directory on a server, from the bundle in a browser, from an overlay or the
 *  editor's buffer in both. Returning `undefined` means "nothing provides that name",
 *  which leaves the mob on its archetype (E-50 kin: a dangling name never breaks it). */
export interface MobScriptSources {
  sourceFor(name: string): string | undefined;
  /** §27.12's trust tier for a name's source (M47.2) — a server answers it from the
   *  selected collection's hash pin, so an un-allowlisted collection's scripts install
   *  as `foreign` and are refused (E-48) while its mobs keep their archetypes. Absent,
   *  or `undefined` for a name, ⇒ `local`: the pre-M47 behaviour of every host. */
  trustFor?(name: string): ScriptTrust | undefined;
}

/** What one mob dispatch binds. `self.*` is derived from the mob itself, so there is
 *  no way for a caller to hand a dispatch a position that is not the entity's. */
export interface MobBinding {
  /** The whitelisted world/effect verb surface, exactly as a block dispatch gets. */
  host: ScriptHost;
  /** This entity's `self.*`/`mem.*`/`handled()` surface for this ONE dispatch. */
  mob: ScriptMobHost;
  /** The dispatching entity's stable id — §27.8's ordering key, and it orders three
   *  separate things: the cadence group (`entityId % divisor`), the frame ceiling
   *  (consumed in entity-id order) and the rng fork (`fork(mobSeed, entityId)`).
   *  Absent ⇒ no stagger and no rng, because there is nothing to stagger or fork by.
   *
   *  NOTE there is deliberately no `rng` field: §27.8 says the generator is re-derived
   *  every dispatch from (seed, entityId, tick) and never stored, so the runtime
   *  derives it. A caller-supplied one would be a second source of truth for a value
   *  whose whole point is having exactly one. */
  entityId?: number;
  /** The mob TYPE (`Entity.type`). §27.8 quarantines the type, not the entity and not
   *  the script name — two types may name one script and only the failing one should
   *  fall back. Absent ⇒ the script name stands in, so the ladder still works for a
   *  caller that has no type to give. */
  type?: string;
  /** The sim tick this dispatch belongs to — the injected tick source (see the file
   *  header). Absent ⇒ no cadence, no frame ceiling, and an rng that cannot fork per
   *  dispatch. */
  tick?: number;
  /** The world's mob seed, `fork(mobSeed, entityId)`'s parent (§27.8). Wiring, not
   *  per-tick state: the host sets it once. */
  seed?: number;
  /** `MobDef.scriptHz` (§27.8) — absent is the 5 Hz default. Picks BOTH the cadence
   *  divisor and the per-dispatch budget, which is what holds ops/second constant. */
  hz?: MobScriptHz;
  /** The per-hook `event.*` payload (§27.6's table), when the hook carries one: `on
   *  hurt`'s `amount`/`source`, `on signal`'s `payload`/`source`, and so on.
   *
   *  `power` is NOT taken from here — it is the mob's fixed zero. `name` is the hook's
   *  own name for every hook but `signal`, where §27.6 gives the row an `event.name` of
   *  its own (the SIGNAL's name) and `handler` carries `'signal'` instead (D-TF-217). */
  event?: Partial<Pick<EventView, 'amount' | 'source' | 'payload' | 'name'>>;
  /** The `on <name>` block to run when it differs from `event.name` (D-TF-217). */
  handler?: string;
}

/** The mob-hook half of the runtime, as the `entities` phase needs it (§27.4's tick
 *  placement). Narrow on purpose: the entity system needs exactly this one method, so
 *  a recording double in a test — and M45.5's budgeted/quarantining wrapper —
 *  implements one method instead of four. */
export interface MobScriptRuntime {
  /** Apply §27.8's schedule, resolve `script` to source, install it for the MOB slice
   *  (idempotent) and dispatch `hook`. Never throws (E-30): a missing source, a refused
   *  compile and a runtime abort all come back as `{ ok: false, error }`, and the caller
   *  then falls back to the archetype for that dispatch.
   *
   *  A dispatch the SCHEDULE declined — an off-beat cadence tick, a frame-ceiling
   *  deferral, a quarantined type — comes back the same way, with a `ScriptDeferred`
   *  error and `ops: 0`. Deliberately the same shape: the caller's job is identical
   *  either way (run the archetype), and the distinct error class is there for the
   *  diagnostics layer, which is the only side that needs to tell them apart. */
  dispatchMob(script: string, hook: MobHook, bind: MobBinding): ScriptResult;
}

/** One script-carrying cell the tick sweep should consider. The host supplies the
 *  key, so the runtime never needs to know how a key is shaped: a block script keys
 *  by its cell (`cellKey(x,y,z)`), and M45's mob scripts key by script name. */
export interface ScriptCell {
  /** Cache + latch key for this cell. Stable for the life of the block. */
  key: string;
  x: number;
  y: number;
  z: number;
  /** The block entity's stored source (`ScriptData.source`). */
  source: string;
}

/** The per-tick inputs for the `blockEntities`-phase sweep. The host owns the block
 *  entities and the redstone engine; the runtime owns the latch and the dispatch. */
export interface ScriptTickCtx {
  /** Every script cell in the simulated world, in the host's iteration order (that
   *  order is part of the determinism contract — keep it stable). */
  cells: Iterable<ScriptCell>;
  /** Redstone power (0..15) reaching a cell; 0 ⇒ unpowered. Called once per cell per
   *  sweep and reused for the latch, `event.power` and `self.powered` — the three are
   *  the same reading of the same freshly-solved field. */
  powerAt(x: number, y: number, z: number): number;
  /** The verb surface every cell dispatched this sweep runs against. */
  host: ScriptHost;
  /** Lets the host consume a rising edge instead of dispatching, for state the sim
   *  cannot hold: the client's AVI-3 autopilot preset. Return true when the edge was
   *  claimed — the script then does not also run, matching the pre-move behavior. */
  claim?(cell: ScriptCell): boolean;
  /** Scripted entities within `radius` of a point (§27.10's signal delivery). Absent ⇒
   *  no signal reaches anyone, which is the correct behaviour for a host with no entity
   *  system rather than a reason to invent one. The host decides the iteration order;
   *  the runtime sorts by entity id before delivering, so that order cannot leak in. */
  signalTargets?(x: number, y: number, z: number, radius: number): Iterable<SignalTarget>;
  /** The sim tick, for the delivered dispatch's `MobBinding.tick`. */
  tick?: number;
  /** The world's mob seed, for the delivered dispatch's rng fork (§27.8). */
  seed?: number;
}

/** One entry of §27.16's per-script error ring: "`{tick, line, message}`". Both a
 *  compile refusal and a runtime abort land here, because from the author's chair they
 *  are the same question ("why is my sheep not doing that?") asked twice. */
export interface ScriptErrorRecord {
  /** The sim tick the failure happened on — the last tick an injected tick source
   *  reported (§27.8's `MobFrame.tick`). A host that wires none records `UNWIRED`, the
   *  same 0 the cadence and the rng fall back to, rather than reading a clock. */
  readonly tick: number;
  /** 1-based source line, when the error carries a position. Absent for the errors
   *  that have none (an over-budget refusal names its own line; a host verb that
   *  refused at runtime may not). */
  readonly line?: number;
  /** The refusal, exactly as `ScriptError` worded it — positions included, so the
   *  editor never re-derives copy the compiler already wrote. */
  readonly message: string;
}

/** What one recorded effect may carry. Scalars only: these are the arguments the verb
 *  layer already type-checked down to a number, a string or a bool, and a vec3 or a
 *  list would make the list a second, worse copy of the VM's value model. */
export type ScriptEffectArg = number | string | boolean;

/** One effect a dispatch applied, in §27.16's "last-dispatch effects" list.
 *
 *  It names the EFFECT, not the call that produced it: `self.faceAt(…)` lands here as
 *  `self.face` with the yaw it computed, and `self.moveTo`/`self.jump` land as
 *  `self.steer` writes of the MOB-3 scratch (§27.6), because that is what the host was
 *  actually asked to do. And it is recorded AFTER the host returns, with the arguments
 *  the host was handed — a host that declined (an unknown block, a full memory bag)
 *  still shows the attempt, since "I called setblock and nothing happened" is exactly
 *  the confusion this list exists to settle. */
export interface ScriptEffect {
  /** The verb as a script names it (`VERB_SIGNATURES`' keys), or `self.steer` for the
   *  steering scratch, which is a write rather than a verb. */
  readonly verb: string;
  /** Its arguments, in call order. */
  readonly args: readonly ScriptEffectArg[];
}

/** What the editor / an op can see about one installed script (§27.16). */
export interface ScriptDiagnostics {
  /** A program is installed and runnable. With `compileError` ALSO set this is E-44's
   *  state: the last-good program is still running and the refused edit is the error
   *  beside it. */
  installed: boolean;
  /** The installed source was blank — nothing to run, and not an error. */
  empty: boolean;
  /** The refusal from the last `install`, when it failed to compile. Read it with
   *  `installed`: `true` ⇒ the last-good program survived the refusal (E-44, §27.16);
   *  `false` ⇒ nothing runs for this key. */
  compileError?: ScriptError;
  /** VM ops the last dispatch consumed (0 before the first) — §27.16's op read-out,
   *  against `opBudget`. It IS `ScriptResult.ops`, stored rather than recomputed. */
  lastOps: number;
  /** Dispatches run since the RUNNING program was installed. A refused reload does not
   *  reset it, because it did not replace what is running. */
  dispatches: number;
  /** §27.16's error ring: at most `SCR.ERROR_RING` entries, OLDEST FIRST, with the
   *  oldest evicted to make room. It outlives a reload — the errors are why the author
   *  is editing — and holds no schedule deferrals, only real failures. */
  errors: readonly ScriptErrorRecord[];
  /** Every effect the last dispatch applied, in the order it applied them. Empty
   *  before the first dispatch, and for a dispatch that reached no verb. */
  lastEffects: readonly ScriptEffect[];
  /** §27.16's clipped counts: what §27.10's cascade bounds dropped on this script's
   *  behalf. Cumulative since the last reset, and zero for a script that has never
   *  caused a clip — a cascade being clipped must be visible to its author (E-46). */
  clipped: ClippedCounts;
  /** The op budget this entry was compiled against AND runs under — `SCR.OP_BUDGET`
   *  for a block, `SCR.MOB.OP_BUDGET` (quartered at `scriptHz: 20`) for a mob. It is
   *  one stored number rather than two computations, which is how §27.3's static gate
   *  and the VM ceiling are kept identical (D-TF-171). 0 ⇒ nothing is installed. */
  opBudget: number;
}

/** The runtime contract (ARCH §27.4 — binding), plus §27.6's mob-hook entry. */
export interface ScriptSystem extends MobScriptRuntime {
  /** Compile+cache by (sourceKey, hostKind); returns a refusal on error/over-bound.
   *  `hz` is §27.8's mob cadence tier and selects the budget the bound is refused
   *  against; omitted (and always, for a block) it is the 5 Hz default. `trust` is
   *  §27.12's tier (M47.1): omitted it is `local`, the op-authored tier every pre-M47
   *  install ran at; `collection`/`foreign` gate the COMPUTED capability set and a
   *  foreign script runs at `SCR.TIER.FOREIGN_OP_BUDGET` (E-48). */
  install(key: string, source: string, host: ScriptHostKind, hz?: MobScriptHz, trust?: ScriptTrust): InstallResult;
  /** Dispatch one event. Returns ops used and any abort. Never throws. */
  dispatch(key: string, ev: ScriptEvent, bind: ScriptBinding): ScriptResult;
  /** Per-tick entry, called from the tick pipeline. Drains the cascade queue. */
  tick(ctx: ScriptTickCtx): void;
  /** Queue one §27.9 lifecycle dispatch for the next cascade drain.
   *
   *  Returns whether it was queued: `false` means §27.10 refused it — past
   *  `MAX_DEPTH`, or the tick's `MAX_DISPATCH` is already spoken for — and the refusal
   *  has been counted against `d.cause`. Nothing dispatches here; that is the point
   *  (§27.9: notifications are queued, never synchronous). */
  enqueueLifecycle(d: LifecycleDispatch): boolean;
  /** The cascade depth of the dispatch currently running (0 when none is). A host's
   *  `emit` passes this as its signal's `depth`, so a chain raised from inside a
   *  handler really does get deeper and `SCR.CASCADE.MAX_DEPTH` really does stop it. */
  readonly dispatchDepth: number;
  /** Queue one §27.10 signal for the next drain. Returns whether it was queued; false
   *  means depth or the shared per-tick dispatch budget refused it, counted as an
   *  `emits` clip against `s.cause`. This is what `ScriptHost.emit` forwards to. */
  enqueueSignal(s: PendingSignal): boolean;
  diagnostics(key: string): ScriptDiagnostics;
}

/** The handler a redstone rising edge selects (`on redstone { … }`). */
const REDSTONE_EVENT = 'redstone';

/** A lifecycle dispatch carries no redstone reading — it was not power that fired it —
 *  so `event.power` is 0 and `self.powered` is false, together rather than separately. */
const NO_POWER = 0;

/** Build the `event.*` view one queued lifecycle dispatch presents (D-TF-208's table).
 *
 *  Only `neighbor` carries a payload: `event.source` is the vec3 of the face-adjacent
 *  cell that changed. The other three carry `name` and `power` alone, and a script that
 *  reads `event.source` inside them aborts with a message naming the hook rather than
 *  receiving a fabricated position. */
function eventFor(d: LifecycleDispatch): ScriptEvent {
  const ev: ScriptEvent = { name: d.hook, power: NO_POWER };
  if (d.hook === 'neighbor' && d.from) return { ...ev, source: vec3(d.from.x, d.from.y, d.from.z) };
  return ev;
}
/** The message a dispatch against a blank source reports — kept verbatim from the
 *  pre-move `boot.ts` path so editor/chat copy does not change. */
const NOTHING_TO_RUN = 'nothing to run';
/** A mob is not a cell and has no redstone reading, so every mob dispatch carries
 *  `event.power` 0 and `self.powered` false. Named rather than inlined because it is a
 *  statement about the mob slice, not an arbitrary zero. */
const MOB_POWER = 0;

/** The one hook §27.8's cadence and frame ceiling govern — the periodic one. The other
 *  five are event-driven and dispatch when their event happens. */
const PERIODIC_HOOK: MobHook = 'tick';

/** §27.8's `scriptHz: 20` opt-in. It IS `TICK.TPS`: "20 Hz" means "every tick" only
 *  because the sim runs at 20 TPS, so deriving it from the tick rate is what stops the
 *  two from drifting if §5.5's rate is ever retuned. */
const HZ_EVERY_TICK: MobScriptHz = TICK.TPS;

/** The seed and the tick a dispatch falls back to when the host wired neither. Both
 *  are legitimate values, not sentinels — the stream stays seeded and deterministic —
 *  but with no tick to fork on an entity re-draws the same sequence every dispatch.
 *  That is the same missing wiring that leaves the cadence and the ceiling inactive
 *  (see the file header), so it is one gap to close, not three. */
const UNWIRED = 0;

/** The cache key a mob script installs under. Namespaced because a block keys by its
 *  CELL (`cellKey(x,y,z)`) and a mob by its script NAME: `'7,70,7'` and `'greeter'`
 *  cannot collide today, but one shared `Map` and two key shapes is exactly the bug
 *  that only shows up once someone names a script after a coordinate.
 *
 *  The CADENCE TIER rides in the key because it changes the budget the source's static
 *  bound is refused against (§27.8). One cache entry holds one budget, so two mob types
 *  naming the same script at different rates must not share one — otherwise whichever
 *  installed first would silently decide the other's gate. The default tier keeps the
 *  pre-M45.5 key byte-for-byte, so an existing `mobScriptKey(name)` lookup still finds
 *  its entry. */
export function mobScriptKey(script: string, hz?: MobScriptHz): string {
  return hz === HZ_EVERY_TICK ? `mob:${script}@${hz}hz` : `mob:${script}`;
}

/** Ticks between two dispatches of one entity (§27.8). `scriptHz: 20` ⇒ 1 (every
 *  tick); anything else is `SCR.MOB.TICK_DIVISOR`, the 5 Hz default. */
function divisorFor(hz: MobScriptHz | undefined): number {
  return hz === HZ_EVERY_TICK ? TICK.TPS / HZ_EVERY_TICK : SCR.MOB.TICK_DIVISOR;
}

/** Scale a per-dispatch mob allowance to a cadence tier so the PER-SECOND allowance is
 *  unchanged (§27.8: "opting in to 20 Hz quarters the per-dispatch budget … without
 *  that coupling the opt-in is a 4× budget rise wearing a latency costume").
 *
 *  "Quarters" is written here as "× divisor ÷ the default divisor" rather than as a
 *  literal 4: the two are the same number by construction, so retuning
 *  `SCR.MOB.TICK_DIVISOR` moves the cadence and the budget together instead of
 *  decoupling them. `allowance × TICK.TPS / divisor` — the per-second cost — is
 *  therefore constant across tiers. */
function perTier(allowance: number, hz: MobScriptHz | undefined): number {
  return (allowance * divisorFor(hz)) / SCR.MOB.TICK_DIVISOR;
}

/** Non-negative remainder. Entity ids and ticks are non-negative in practice; this is
 *  E-33 hygiene so a hostile or corrupt id cannot land a dispatch in a negative group
 *  that matches nothing (or, worse, matches inconsistently between two runs). */
function groupOf(n: number, divisor: number): number {
  return ((n % divisor) + divisor) % divisor;
}

/** Nothing provides a script name — the default, so a host that never wires
 *  `data/scripts/` gets today's behavior (every mob on its archetype) rather than an
 *  error per mob per tick. */
const NO_SOURCES: MobScriptSources = { sourceFor: () => undefined };

/** One cache entry: the source last OFFERED to this key, the slice and cadence tier it
 *  was compiled for, the program actually RUNNING (null for a blank source, or for a
 *  refused one with no last-good program behind it), the limits it was gated against,
 *  and this key's counters and diagnostics.
 *
 *  `source` and `program` are two facts, not one (§27.16/E-44): after a refused reload
 *  the source is the broken text the author just saved and the program is the last one
 *  that compiled, still running. */
interface Entry {
  source: string;
  hostKind: ScriptHostKind;
  /** The cadence tier this entry's limits were computed from (mobs only). */
  hz: MobScriptHz | undefined;
  /** The §27.12 trust tier this entry was admitted at — it decides both the slice the
   *  computed capability set was gated against and the budget below. */
  trust: ScriptTrust;
  program: Program | null;
  result: InstallResult;
  lastOps: number;
  dispatches: number;
  /** §27.16's ring, oldest first. Carried across a reload of the same key — it is the
   *  script's error history, not the program's. */
  errors: ScriptErrorRecord[];
  /** The effects of the last dispatch that reached the VM. */
  lastEffects: readonly ScriptEffect[];
  /** §27.3's static gate AND the VM's runtime ceiling, as ONE number computed once. */
  opBudget: number;
  /** §27.5's per-dispatch `setblock` ceiling, scaled to the cadence tier for the same
   *  reason the op budget is: at 20 Hz a mob gets four times as many dispatches, so an
   *  unscaled ceiling would quadruple how much world it edits per second. */
  setblockMax: number;
}

/** The per-dispatch op budget a host kind's scripts are measured against (§27.3), and
 *  the ONE place the kinds are distinguished (D-TF-171's resolution says so in as many
 *  words). A mob is measured against `SCR.MOB.OP_BUDGET` — ~20× tighter than a block's,
 *  because MOB-4 allows ~108 of them and a block script is edge-triggered.
 *
 *  Callers do not use this twice per dispatch: `install` calls it and stores the result
 *  on the entry, and `dispatch` reads the entry. That is deliberate — §27.3's promise
 *  is that a script clearing the save-time gate cannot then abort on ops, and that only
 *  holds while the gate and the ceiling are literally the same number. */
function budgetFor(host: ScriptHostKind, hz?: MobScriptHz, trust: ScriptTrust = LOCAL_TRUST): number {
  // §27.12: a FOREIGN script runs at `SCR.TIER.FOREIGN_OP_BUDGET` whichever host it is
  // for (D-TF-107 — a reduced slice removes reach, not CPU), and a foreign mob pays the
  // same cadence quartering every mob allowance pays, so its ops/second stay constant
  // too. `Math.min` keeps the tier from ever RAISING a budget.
  if (trust.tier === 'foreign') {
    const foreign = host === 'mob' ? perTier(SCR.TIER.FOREIGN_OP_BUDGET, hz) : SCR.TIER.FOREIGN_OP_BUDGET;
    return Math.min(foreign, budgetFor(host, hz));
  }
  return host === 'mob' ? perTier(SCR.MOB.OP_BUDGET, hz) : SCR.OP_BUDGET;
}

/** Would `install` admit this source — the SAME gate, run without installing anything?
 *  §27.14's server re-validation (M47.4) calls this rather than trusting a client's
 *  compile, and it is this function rather than a copy of it so the two cannot drift: a
 *  push the server accepts is exactly a source `install` will then accept. A blank
 *  source is admitted as empty, as `install` admits it. */
export function checkInstall(
  source: string, host: ScriptHostKind, hz?: MobScriptHz,
  trust: ScriptTrust = LOCAL_TRUST, policy: TierPolicy = SHIPPED_TIER_POLICY,
): InstallResult {
  if (source.trim() === '') return { ok: true, empty: true };
  const compiled = compileForInstall(source, budgetFor(host, hz, trust), host, trust, policy);
  return compiled.ok ? { ok: true, empty: false } : { ok: false, error: compiled.error };
}

/** Compile a source for one install: the host slice (§27.5), then the TRUST TIER over the
 *  program's COMPUTED capability set (§27.12), then the static bound (§27.3).
 *
 *  The tier runs BEFORE the bound on purpose: a foreign script on a server with the tier
 *  shut must be told the tier is shut (E-48, naming what it would have used), not that it
 *  is too expensive for a budget it was never going to be given. A `local` install is
 *  exactly `tryCompile(source, budget, host)` — the pre-M47 path, unchanged. */
function compileForInstall(
  source: string, budget: number, host: ScriptHostKind, trust: ScriptTrust, policy: TierPolicy,
): CompileResult {
  if (trust.tier === 'local') return tryCompile(source, budget, host);
  const compiled = tryCompile(source, undefined, host);
  if (!compiled.ok) return compiled;
  const refusal = checkTier(trust, programCapabilities(compiled.program), policy);
  if (refusal) return { ok: false, error: refusal };
  const cost = programCost(compiled.program, budget);
  if (overBudget(cost, budget)) return { ok: false, error: overBudgetError(cost, budget) };
  return compiled;
}

/** The two empty diagnostics lists, shared and frozen: `diagnostics()` spreads
 *  `EMPTY_DIAGNOSTICS` for an unknown key, and a shared mutable array would hand every
 *  such caller the same one to write into. */
const NO_ERRORS: readonly ScriptErrorRecord[] = Object.freeze([]);
const NO_EFFECTS: readonly ScriptEffect[] = Object.freeze([]);

const EMPTY_DIAGNOSTICS: ScriptDiagnostics = {
  installed: false, empty: false, lastOps: 0, dispatches: 0, opBudget: 0,
  errors: NO_ERRORS, lastEffects: NO_EFFECTS, clipped: NO_CLIPS,
};

/** §27.8's per-TYPE failure ladder. Keyed by mob type — not by entity (one broken mob
 *  is the type's script being broken) and not by script name (two types may name one
 *  script, and only the one that actually failed should fall back).
 *
 *  Never serialized, by construction: it lives in a `Map` on the runtime, so a
 *  save→load builds a world whose ladder starts clean (§27.18's last row). */
interface TypeState {
  /** The cache key these aborts came from. `install` resets every state carrying the
   *  key it replaces, which is how §27.8's "an edit clears it instantly" works. */
  key: string;
  /** Consecutive aborted dispatches; any clean dispatch resets it to 0. */
  aborts: number;
  /** Latched at `SCR.MOB.QUARANTINE_K`, and the latch is where the ONE chat line is
   *  emitted — every later dispatch returns before reaching it, which is what makes
   *  "one line, never one per abort" structural rather than a flag to remember. */
  quarantined: boolean;
}

/** The three schedule refusals, pre-built. At 20 TPS with ~100 scripted mobs the
 *  off-beat case alone is ~1 500 refusals a second, and minting an `Error` (with its
 *  stack capture) for each would cost more than the dispatches they stand in for.
 *  Nothing on any path mutates a `ScriptError`, so one shared instance is safe. */
const OFF_BEAT: ScriptResult = Object.freeze({
  ok: false, ops: 0, error: new ScriptDeferred('not this entity\'s dispatch tick (§27.8 cadence)'),
});
const OVER_CEILING: ScriptResult = Object.freeze({
  ok: false, ops: 0, error: new ScriptDeferred('deferred — the tick\'s script op ceiling is spent (§27.8)'),
});
const IS_QUARANTINED: ScriptResult = Object.freeze({
  ok: false, ops: 0, error: new ScriptDeferred('type quarantined after repeated script errors (§27.8)'),
});

/** The single chat line §27.8 allows when a type is quarantined. It names the script,
 *  the type and the way out, because the player who sees it is the one who can fix it. */
function quarantineLine(script: string, type: string): string {
  return `script '${script}' quarantined after ${SCR.MOB.QUARANTINE_K} errors in a row — every ${type} is running its default behaviour until the script is edited`;
}

/** §27.8's per-dispatch rng: `scriptRng(fork(mobSeed, entityId), tick)`, built here and
 *  dropped when the dispatch ends. Nothing stores it — there is no cursor to save, so
 *  nothing to rehydrate on load, nothing a hot reload disturbs and nothing to drift
 *  between server and replay.
 *
 *  No entity id ⇒ no rng, and `random`/`chance` then abort rather than fall back to an
 *  unseeded source: that is the same fail-closed rule `api.ts` already applies to an
 *  absent host method. */
function rngFor(bind: MobBinding): ScriptRng | undefined {
  if (bind.entityId === undefined) return undefined;
  return scriptRng(entityScriptSeed(bind.seed ?? UNWIRED, bind.entityId), bind.tick ?? UNWIRED);
}

/** §27.16's last-dispatch effects list, recorded at the ONE seam that sees every effect
 *  a dispatch can have: the two surfaces `dispatch` hands the VM. Wrapping them here —
 *  rather than asking `api.ts` to report, or every host to record — is what makes the
 *  list complete by construction: a verb reaches the world through one of these methods
 *  or it does not reach it at all, so a verb added later cannot quietly escape the
 *  read-out.
 *
 *  Only EFFECTS are recorded. A read (`getblock`, `detect`, `nearestPlayer`, `mem.get`,
 *  `self.pathToward`) is forwarded untouched: a list of what a script LOOKED at would
 *  bury what it DID, once per mob, five times a second.
 *
 *  Every wrapper delegates with an EXPLICIT receiver (`inner.method(…)`) and never
 *  rebinds `this`, so a host that keeps its own state — the recording doubles in the
 *  tests, `client/boot.ts`'s closure host, the server's — behaves identically wrapped.
 *  An OPTIONAL host read (§27.5's `nearestPlayer`/`playerWithin`/`entityWithin`,
 *  D-TF-197) is re-exposed only when the inner host really implements it: `api.ts`
 *  fails closed on absence, and a wrapper that always defined the three would quietly
 *  re-open that door. */
class EffectRecorder {
  readonly effects: ScriptEffect[] = [];

  private push(verb: string, ...args: ScriptEffectArg[]): void {
    this.effects.push({ verb, args });
  }

  /** The world/effect surface (SCR-2's `ScriptHost`), wrapped. */
  host(inner: ScriptHost): ScriptHost {
    const rec: ScriptHost = {
      getBlock: (x, y, z) => inner.getBlock(x, y, z),
      setBlock: (x, y, z, name) => {
        const set = inner.setBlock(x, y, z, name);
        this.push('setblock', x, y, z, name);
        return set;
      },
      fill: (x1, y1, z1, x2, y2, z2, name) => {
        const cells = inner.fill(x1, y1, z1, x2, y2, z2, name);
        this.push('fill', x1, y1, z1, x2, y2, z2, name);
        return cells;
      },
      summon: (mob, x, y, z, script) => {
        // Forwarded with the SAME arity it arrived with, so a host that counts its
        // arguments sees exactly the call the verb layer made.
        const spawned = script === undefined ? inner.summon(mob, x, y, z) : inner.summon(mob, x, y, z, script);
        if (script === undefined) this.push('summon', mob, x, y, z);
        else this.push('summon', mob, x, y, z, script);
        return spawned;
      },
      give: (item, count) => {
        const given = inner.give(item, count);
        this.push('give', item, count);
        return given;
      },
      say: (message) => { inner.say(message); this.push('say', message); },
      detect: (x, y, z, name) => inner.detect(x, y, z, name),
      nearestEntity: (x, y, z, radius) => inner.nearestEntity(x, y, z, radius),
      setAircraftControl: (pitch, roll, throttle) => {
        const flying = inner.setAircraftControl(pitch, roll, throttle);
        this.push('aircraft.setControl', pitch, roll, throttle);
        return flying;
      },
    };
    const { nearestPlayer, playerWithin, entityWithin, emit } = inner;
    // `emit` is optional (D-TF-197's rule), so it is forwarded only when the host has
    // one — and it MUST be forwarded, or the wrapper would silently strip the verb and
    // every `emit` would abort with "this host cannot raise signals".
    // The payload is recorded as a string: §27.16's effect args are scalars, and a vec3
    // payload has no scalar form, so it is rendered the way `say` renders one.
    if (emit) {
      rec.emit = (name, payload, x, y, z) => {
        const queued = emit.call(inner, name, payload, x, y, z);
        this.push('emit', name, valueToString(payload));
        return queued;
      };
    }
    if (nearestPlayer) rec.nearestPlayer = (x, y, z) => nearestPlayer.call(inner, x, y, z);
    if (playerWithin) rec.playerWithin = (x, y, z, radius) => playerWithin.call(inner, x, y, z, radius);
    if (entityWithin) rec.entityWithin = (x, y, z, radius, kind) => entityWithin.call(inner, x, y, z, radius, kind);
    return rec;
  }

  /** The mob surface (§27.5's `ScriptMobHost`), wrapped. The pose stays a GETTER rather
   *  than a snapshot so a read still sees the entity, and `steering` is WRAPPED rather
   *  than passed through: `self.moveTo`/`moveAway`/`jump` write that MOB-3 scratch
   *  directly (§27.6), so a pass-through would make movement the one effect the list
   *  could not see. */
  mob(inner: ScriptMobHost): ScriptMobHost {
    const push = (verb: string, ...args: ScriptEffectArg[]): void => { this.push(verb, ...args); };
    const scratch = inner.steering;
    const steering: MobSteering = {
      get wishX(): number { return scratch.wishX; },
      set wishX(v: number) { scratch.wishX = v; push('self.steer', 'wishX', v); },
      get wishZ(): number { return scratch.wishZ; },
      set wishZ(v: number) { scratch.wishZ = v; push('self.steer', 'wishZ', v); },
      get wishJump(): boolean { return scratch.wishJump; },
      set wishJump(v: boolean) { scratch.wishJump = v; push('self.steer', 'wishJump', v); },
    };
    return {
      get x(): number { return inner.x; },
      get y(): number { return inner.y; },
      get z(): number { return inner.z; },
      steering,
      pathToward: (goalX, goalZ) => inner.pathToward(goalX, goalZ),
      moveSpeed: () => inner.moveSpeed(),
      face: (yaw) => { inner.face(yaw); push('self.face', yaw); },
      setTarget: (id) => {
        const targeted = inner.setTarget(id);
        push('self.setTarget', id);
        return targeted;
      },
      clearTarget: () => { inner.clearTarget(); push('self.clearTarget'); },
      attack: () => {
        const hit = inner.attack();
        push('self.attack');
        return hit;
      },
      hurt: (amount) => { inner.hurt(amount); push('self.hurt', amount); },
      heal: (amount) => { inner.heal(amount); push('self.heal', amount); },
      despawn: () => { inner.despawn(); push('self.despawn'); },
      memGet: (key) => inner.memGet(key),
      memSet: (key, value) => {
        const stored = inner.memSet(key, value);
        push('mem.set', key, value);
        return stored;
      },
      handled: () => { inner.handled(); push('handled'); },
    };
  }
}

class Runtime implements ScriptSystem {
  constructor(
    private readonly sources: MobScriptSources,
    /** §27.12's server-wide tier switches (the shipped policy keeps foreign shut). */
    private readonly policy: TierPolicy,
  ) {}

  /** key → compiled entry. One entry per key: reinstalling the same key with a
   *  different source OR a different host kind recompiles, which is §27.4's
   *  (sourceKey, hostKind) cache with the kind carried on the entry. */
  private readonly cache = new Map<string, Entry>();
  /** The rising-edge latch: keys that were powered at the end of the last sweep.
   *  Moved verbatim from `boot.ts` — a script runs once per rising edge, never while
   *  power is merely held. A key for a cell that was broken while powered stays in
   *  the set (the pre-move behavior); M46's `on broken` lifecycle hook is where that
   *  gets cleared, so it is preserved here rather than silently changed. */
  private readonly powered = new Set<string>();

  /** §27.8's per-type failure ladder, `Entity.type` → state. */
  private readonly types = new Map<string, TypeState>();
  /** The tick the frame ledger below belongs to; `undefined` until a dispatch arrives
   *  carrying an injected tick, which is also the state in which there is no frame to
   *  put a ceiling on. */
  private frameTick: number | undefined;
  /** Mob script ops spent so far in `frameTick`, against `SCR.MOB.FRAME_CEILING`. */
  private frameOps = 0;

  /** §27.9's queued lifecycle dispatches, drained once per `tick` (§27.4: "Cascade
   *  drain runs once per phase, bounded by §27.10"). An ARRAY, drained in insertion
   *  order: the order decides who is still inside `MAX_DISPATCH` when it clips, so it
   *  has to be a pure function of the order things happened, never of `Map` iteration. */
  private cascade: LifecycleDispatch[] = [];
  /** §27.10's raised-but-undelivered signals, drained with the cascade. */
  private signals: PendingSignal[] = [];
  /** The cascade depth of the dispatch currently running, or 0 when none is.
   *
   *  A host's `emit` has to queue at the RIGHT depth, and it has no way to know one:
   *  `ScriptHost` is a single object shared across every dispatch in a tick, and the
   *  verb layer gives it a position but not a depth. So the runtime — which is the side
   *  that knows which dispatch it is inside — publishes it, and the host reads it back
   *  through `dispatchDepth`. Without this a signal raised from inside an `on signal`
   *  handler would re-queue at depth 0 forever and `MAX_DEPTH` would never bite. */
  private depth = 0;

  /** The tick `spawnsUsed` belongs to, or `undefined` when no dispatch has reported
   *  one. §27.10's spawn budget is per TICK and shared across every script, so it
   *  cannot live on a per-dispatch ctx. */
  private spawnTick: number | undefined;
  /** Script-caused spawns already attempted this tick, against `SPAWN_BUDGET`. */
  private spawnsUsed = 0;

  /** Start a fresh spawn budget when the tick has moved on.
   *
   *  Called from two places, and it needs both: `tick()`, which is the pipeline's own
   *  per-tick boundary, and every dispatch that carries an injected tick. The second is
   *  not belt-and-braces — a host that runs MOB scripts but installs no block script
   *  never calls `tick()`, and without the dispatch-side roll its eight spawns would be
   *  spent once and never refilled. */
  private rollSpawnTick(tick: number | undefined): void {
    if (tick === undefined || tick === this.spawnTick) return;
    this.spawnTick = tick;
    this.spawnsUsed = 0;
  }

  /** Take one spawn from this tick's budget (§27.10). False ⇒ spent, and counted. */
  private chargeSpawn(tick: number | undefined, cause: string | undefined): boolean {
    this.rollSpawnTick(tick);
    if (this.spawnsUsed >= SCR.CASCADE.SPAWN_BUDGET) {
      this.clip(cause, 'spawns');
      return false;
    }
    this.spawnsUsed++;
    return true;
  }
  /** Per-script clip tallies (§27.16). Keyed by the CAUSING script's key. */
  private readonly clips = new Map<string, { dispatches: number; emits: number; spawns: number }>();

  /** Bump one clip counter for the script that caused the drop. An enqueue with no
   *  cause — a world-caused event like a player breaking a block — is still refused and
   *  still bounded, but there is no author to show it to, so it is not attributed. */
  private clip(cause: string | undefined, kind: 'dispatches' | 'emits' | 'spawns'): void {
    if (cause === undefined) return;
    const tally = this.clips.get(cause) ?? { dispatches: 0, emits: 0, spawns: 0 };
    tally[kind]++;
    this.clips.set(cause, tally);
  }

  /** This script's clip tallies, copied out — the editor reads counts, it does not get
   *  a handle on the runtime's own. */
  private clippedFor(key: string): ClippedCounts {
    const tally = this.clips.get(key);
    return tally ? { ...tally } : NO_CLIPS;
  }

  /** Dispatches already queued for the next drain. §27.10's `MAX_DISPATCH` is "across
   *  all scripts **and all sources**", so lifecycle and signals share ONE budget rather
   *  than getting a cap each — two caps would let a cascade that alternates between
   *  them spend twice what the section allows. */
  private pendingDispatches(): number {
    return this.cascade.length + this.signals.length;
  }

  enqueueLifecycle(d: LifecycleDispatch): boolean {
    // Bound 1 — depth (§27.10). A chain four links long is a reaction; longer is a loop
    // wearing one as a disguise, so it is dropped HERE rather than allowed to queue and
    // then consume a dispatch slot that a shallower cascade could have used.
    if (d.depth > SCR.CASCADE.MAX_DEPTH) {
      this.clip(d.cause, 'dispatches');
      return false;
    }
    // Bound 2 — the per-tick dispatch cap (§27.10), applied at ENQUEUE so that a `fill`
    // of `SCR.FILL_MAX` cells cannot first build a 24 576-entry queue and only then
    // discover it may run 256 of them. The cap is what the queue is allowed to HOLD for
    // one tick, because the drain runs the whole queue and the queue is cleared after.
    if (this.pendingDispatches() >= SCR.CASCADE.MAX_DISPATCH) {
      this.clip(d.cause, 'dispatches');
      return false;
    }
    this.cascade.push(d);
    return true;
  }

  get dispatchDepth(): number {
    return this.depth;
  }

  enqueueSignal(s: PendingSignal): boolean {
    // An `emit` DELIVERS at depth + 1 (§27.10), so that is the depth the bound is
    // tested against — testing the emitter's own depth would let a chain run one link
    // longer than the section allows.
    if (s.depth + 1 > SCR.CASCADE.MAX_DEPTH) {
      this.clip(s.cause, 'emits');
      return false;
    }
    if (this.pendingDispatches() >= SCR.CASCADE.MAX_DISPATCH) {
      this.clip(s.cause, 'emits');
      return false;
    }
    this.signals.push(s);
    return true;
  }

  /** Deliver the queued signals (§27.10) and clear the queue.
   *
   *  Three things happen here, in this order, and the order is the determinism
   *  contract: resolve each signal's recipients, sort the whole delivery list by
   *  **entity id then depth**, then dedupe by (entity, signal NAME) — the rule that
   *  kills A↔B ping-pong, since an entity two emitters shout the same word at wakes up
   *  once. Dedupe AFTER the sort so that which of the duplicates survives is the
   *  shallowest one at the lowest id, and not whichever the host happened to yield. */
  private drainSignals(ctx: ScriptTickCtx): void {
    if (this.signals.length === 0) return;
    const batch = this.signals;
    this.signals = [];
    if (!ctx.signalTargets) return; // nobody can receive: the host has no entity index

    const deliveries: Array<{ target: SignalTarget; signal: PendingSignal }> = [];
    for (const signal of batch) {
      for (const target of ctx.signalTargets(signal.x, signal.y, signal.z, SCR.SIGNAL.RADIUS)) {
        deliveries.push({ target, signal });
      }
    }
    deliveries.sort((a, b) =>
      a.target.entityId - b.target.entityId || a.signal.depth - b.signal.depth);

    const delivered = new Set<string>();
    for (const { target, signal } of deliveries) {
      const once = `${target.entityId}\u0000${signal.name}`;
      if (delivered.has(once)) {
        this.clip(signal.cause, 'emits');
        continue;
      }
      delivered.add(once);
      // Delivery is one deeper than the raise (§27.10), and a signal raised from
      // inside this handler inherits that — which is what makes a chain terminate.
      this.depth = signal.depth + 1;
      this.dispatchMob(target.script, 'signal', {
        host: ctx.host,
        mob: target.mob,
        entityId: target.entityId,
        type: target.type,
        hz: target.hz,
        tick: ctx.tick,
        seed: ctx.seed,
        // §27.6's `on signal` row: `name`, `payload`, `source`. `source` is WHERE it
        // was raised, which is the only thing a receiver can act on — §27.10 gives it
        // no emitter identity to reply to, by design.
        // §27.6's `on signal` row: `name` (the SIGNAL's name, which §5.1's `pack.tfs`
        // compares against), `payload`, `source` (where it was raised — §27.10 gives a
        // receiver no emitter identity to reply to, by design).
        event: { name: signal.name, payload: signal.payload, source: vec3(signal.x, signal.y, signal.z) },
        handler: 'signal',
      });
      this.depth = 0;
    }
  }

  /** Run the queued lifecycle dispatches and clear the queue (§27.4's cascade drain).
   *
   *  The queue is SNAPSHOTTED and cleared before the first dispatch, so a handler that
   *  enqueues while it runs feeds the NEXT tick's drain rather than extending this one.
   *  That is what makes the per-tick cap a real bound instead of a speed limit a
   *  sufficiently chatty cascade could out-run. */
  private drainCascade(host: ScriptHost, tick: number | undefined): void {
    if (this.cascade.length === 0) return;
    const batch = this.cascade;
    this.cascade = [];
    for (const d of batch) {
      // `on broken` runs for a cell whose block entity is already gone, which is why the
      // source rides on the dispatch. Installing per dispatch is idempotent and cheap
      // (same source ⇒ cache hit), and it is what lets a just-edited script take effect.
      const installed = this.install(d.key, d.source, 'block');
      if (!installed.ok || (installed.ok && installed.empty)) continue;
      this.depth = d.depth;
      this.dispatch(d.key, eventFor(d), {
        host,
        tick,
        // A lifecycle dispatch carries no redstone reading: it was not power that
        // fired it. `self.powered` is therefore false, matching `event.power` 0.
        self: { x: d.x, y: d.y, z: d.z, powered: false },
      });
      this.depth = 0;
    }
  }

  install(
    key: string, source: string, host: ScriptHostKind, hz?: MobScriptHz, trust: ScriptTrust = LOCAL_TRUST,
  ): InstallResult {
    const prev = this.cache.get(key);
    // Idempotent: the same source for the same slice at the same cadence tier and the
    // same trust is already compiled. This is what keeps the tick sweep from recompiling
    // every cell every redstone tick — and, since M45.5, what keeps `dispatchMob` from
    // recompiling a mob script on every one of its dispatches. A TRUST change recompiles
    // (M47.1): it can turn an admitted script into a refused one.
    if (prev && prev.source === source && prev.hostKind === host && prev.hz === hz && sameTrust(prev.trust, trust)) {
      return prev.result;
    }
    const blank = source.trim() === '';
    // §27.3's save-time refusal, enforced HERE because this is the one place that knows
    // the target host — the budget a bound must fit is the host's, not a global. Sound
    // since D-TF-171 corrected the `repeat` arithmetic; `tests/script_cost.test.ts`
    // pins that the bound dominates the VM, which is what makes clearing this gate a
    // real guarantee rather than a hint.
    // The HOST KIND goes to the compiler (M45.3). §27.5's slice is enforced in
    // `parse.ts`, and this is the one call site a real install goes through — without
    // the third argument every source compiled as a `'block'` (parse.ts's fail-closed
    // default), so a mob naming `fill` or `give` was refused only in tests that passed
    // the kind by hand. "The verb table IS the sandbox" is only true if the sandbox
    // knows which table.
    // The budget is computed ONCE, here, and stored: `dispatch` hands the same number
    // to the VM, so §27.3's gate and E-30's ceiling cannot drift apart (D-TF-171).
    // §27.12 (M47.1): the TRUST tier gates the computed capability set and picks the
    // budget; `local` — every pre-M47 install — is the unchanged path.
    const opBudget = budgetFor(host, hz, trust);
    const compiled = blank ? null : compileForInstall(source, opBudget, host, trust, this.policy);
    const result: InstallResult = compiled && !compiled.ok
      ? { ok: false, error: compiled.error }
      : { ok: true, empty: blank };
    // E-44, §27.16's hot reload: a REFUSED reload keeps the LAST-GOOD program running.
    // A broken save must not brick every sheep in the world — the author is mid-edit,
    // and the mob they are editing is alive in front of them. Only a source that
    // COMPILED, or a blank one (which is "nothing to run", never an error), replaces
    // what is running.
    //
    // The last-good program is reusable only while the slice and the cadence tier still
    // match, because those two decide the limits it was gated against (§27.3/§27.8):
    // carrying a program across a tier change would run it under a budget its static
    // bound was never measured for. They match in the case this rule exists for — the
    // same mob type, re-saved — and a mismatch simply falls back to "nothing runs".
    // The trust tier joins them (M47.1): it decides both the slice the program was
    // admitted under and its budget.
    const lastGood = prev && prev.hostKind === host && prev.hz === hz && sameTrust(prev.trust, trust)
      ? prev.program : null;
    const program = compiled === null ? null : compiled.ok ? compiled.program : lastGood;
    // A program was SWAPPED only if what runs after this call differs from what ran
    // before it. A refused reload swaps nothing, and the two counters and the quarantine
    // ladder below all key off that fact rather than off "install was called".
    const swapped = program !== (prev?.program ?? null);
    const entry: Entry = {
      source,
      hostKind: host,
      hz,
      trust,
      program,
      result,
      // The counters describe the RUNNING program, so they reset when it changes and
      // survive when it does not — "0 dispatches" beside a program that has run a
      // thousand times would be a read-out that lies.
      lastOps: swapped ? 0 : prev?.lastOps ?? 0,
      dispatches: swapped ? 0 : prev?.dispatches ?? 0,
      opBudget,
      setblockMax: perTier(SCR.MOB.SETBLOCK_MAX, hz),
      // §27.16's ring is the SCRIPT's history, not the program's: it is carried across
      // every reload of this key, because the last sixteen errors are precisely what an
      // author is editing in response to.
      errors: prev?.errors ?? [],
      lastEffects: swapped ? NO_EFFECTS : prev?.lastEffects ?? NO_EFFECTS,
    };
    this.cache.set(key, entry);
    // The refusal goes into the ring HERE, which is also the only place it goes: the
    // early return above makes `install` idempotent per source, so a broken script
    // re-installed on every one of its dispatches records its refusal once instead of
    // five times a second (which would evict every other error in the ring).
    if (!result.ok) this.record(entry, result.error);
    // §27.8: "an edit clears it instantly". This is the one place a program is
    // REPLACED — `dispatchMob` re-resolves its name every dispatch and lands here the
    // moment the source text changes — so the ladder is reset from the one event that
    // means "what runs is now different". A REFUSED edit is deliberately not that
    // event: nothing changed, so there is nothing to forgive, and clearing would hand
    // a quarantined broken program `QUARANTINE_K` more chances to fail for free.
    // Clearing a set of records is order-independent, so no `Map` iteration order can
    // reach an outcome (ENG-2).
    if (swapped) {
      for (const state of this.types.values()) {
        if (state.key !== key) continue;
        state.aborts = 0;
        state.quarantined = false;
      }
    }
    return result;
  }

  /** Append one failure to this key's §27.16 ring, evicting the OLDEST to stay at
   *  `SCR.ERROR_RING`. An array appended at the back and evicted from the front is the
   *  whole mechanism — deterministic by construction, and nothing in the sim reads it
   *  back, so it cannot reach an outcome (ENG-2).
   *
   *  A `ScriptDeferred` is never recorded: §27.8's schedule refusals (off-beat cadence,
   *  frame ceiling, quarantine) are not failures of the script, and at 5 Hz they would
   *  outnumber real aborts three to one and push them out of the ring. */
  private record(entry: Entry, error: ScriptError): void {
    if (error instanceof ScriptDeferred) return;
    entry.errors.push({
      tick: this.frameTick ?? UNWIRED,
      ...(error.pos ? { line: error.pos.line } : {}),
      message: error.message,
    });
    if (entry.errors.length > SCR.ERROR_RING) entry.errors.shift();
  }

  dispatch(key: string, ev: ScriptEvent, bind: ScriptBinding): ScriptResult {
    const entry = this.cache.get(key);
    if (!entry) return { ok: false, ops: 0, error: new ScriptError(`no script installed for '${key}'`) };
    if (!entry.program) {
      // A blank source, or one whose compile was refused. Both report what they
      // reported before the move; neither throws (E-30).
      return {
        ok: false,
        ops: 0,
        error: entry.result.ok ? new ScriptError(NOTHING_TO_RUN) : entry.result.error,
      };
    }
    // §27.16's last-dispatch effects list: the surfaces the VM is handed are the
    // recorder's, so everything this dispatch asks of the world is observed on its way
    // through. One recorder per dispatch, dropped with it — "last dispatch" means the
    // last one, not a running total.
    const recorder = new EffectRecorder();
    // `run` catches every failure — budget, depth, a bad type, a host method that
    // throws — and hands it back as `{ ok: false, error }`, so this is the seam that
    // guarantees a script error never reaches the tick loop (E-30).
    const res = run(entry.program, recorder.host(bind.host), {
      self: bind.self,
      event: ev,
      // The mob surface + its per-dispatch `setblock` ceiling (§27.5). Both are
      // undefined for a block dispatch, which is exactly the pre-M45.3 call.
      mob: bind.mob ? recorder.mob(bind.mob) : undefined,
      setblockMax: bind.mob ? entry.setblockMax : undefined,
      // The same number `install` refused this source's static bound against (§27.3,
      // D-TF-171): read off the entry, never recomputed, so a mob is measured against
      // `SCR.MOB.OP_BUDGET` at BOTH ends and "clearing the gate guarantees the
      // dispatch fits" stays true. A block entry carries `SCR.OP_BUDGET`, which is what
      // `vm.ts` defaulted to anyway — the block path is byte-for-byte unchanged.
      opBudget: entry.opBudget,
      rng: bind.rng,
      handler: bind.handler,
      // §27.10's per-tick spawn budget, shared across every script (M46.4). The clip is
      // billed to THIS key, which is the script whose `summon` was refused.
      spawn: () => this.chargeSpawn(bind.tick, key),
    });
    // §27.16's op read-out IS the VM's own count, stored rather than recomputed, so the
    // two cannot disagree.
    entry.lastOps = res.ops;
    entry.lastEffects = recorder.effects;
    entry.dispatches++;
    // Effects applied before an abort really happened, so they stay in the list; the
    // abort itself joins the ring beside them. A blank source and a refused compile
    // return above without reaching here — the first is not an error and the second was
    // already recorded at `install`, and re-recording it every dispatch would flush the
    // ring with one repeated line.
    if (!res.ok && res.error) this.record(entry, res.error);
    return res;
  }

  /** §27.6's mob-hook entry, called from the `entities` phase BEFORE the entity's
   *  archetype. Resolve → install → dispatch, in that order, so a hot-swapped source
   *  takes effect on the next dispatch (`install` is idempotent per source).
   *
   *  Whether the script called `handled()` is NOT reported here: `handled()` writes
   *  through `bind.mob`, so the side that built the mob surface already knows, and a
   *  second channel for the same fact could disagree with the first. */
  dispatchMob(script: string, hook: MobHook, bind: MobBinding): ScriptResult {
    // §27.8's schedule first, because the cheap refusals must BE cheap: an entity that
    // is not due this tick costs one remainder and never touches the resolver, the
    // compile cache or the VM. That is the whole point of a 5 Hz cadence.
    const declined = this.schedule(hook, bind);
    if (declined) return declined;
    const source = this.sources.sourceFor(script);
    if (source === undefined) {
      // Not a script failure and NOT a rung on the quarantine ladder: a name that
      // resolves to nothing is a mob with no script yet, not a broken one, and it must
      // still pick up an overlay/editor source the moment one appears.
      return { ok: false, ops: 0, error: new ScriptMissing(`no source for script '${script}'`) };
    }
    const key = mobScriptKey(script, bind.hz);
    // Resolve → install → dispatch, in that order, so a hot-swapped source takes effect
    // on the next dispatch AND clears this type's quarantine on the way through.
    // The source's trust tier (M47.2, §27.12) rides into `install`, which is where a
    // tier can bite: an un-allowlisted collection's script is refused there, and the
    // refusal below falls this mob back to its archetype like any other.
    this.install(key, source, 'mob', bind.hz, this.sources.trustFor?.(script) ?? LOCAL_TRUST);
    const type = bind.type ?? script;
    const state = this.typeState(key, type);
    if (state.quarantined) return IS_QUARANTINED;
    // `power` is the mob's fixed zero. `name` defaults to the hook and is overridden
    // only by `on signal`, whose `event.name` is the signal's name while `bind.handler`
    // names the block to run (D-TF-217).
    const res = this.dispatch(key, { name: hook, ...bind.event, power: MOB_POWER }, {
      host: bind.host,
      // `self.x/y/z` is the mob's own pose — derived here, never passed in.
      self: { x: bind.mob.x, y: bind.mob.y, z: bind.mob.z, powered: false },
      mob: bind.mob,
      rng: rngFor(bind),
      tick: bind.tick,
      handler: bind.handler,
    });
    // Every op a mob spent counts against the tick's ceiling, including an aborted
    // dispatch's and an event hook's — the ceiling exists to protect TPS, and work
    // thrown away is work that was still done.
    if (this.frameTick !== undefined) this.frameOps += res.ops;
    this.ladder(state, res.ok, script, type, bind.host);
    return res;
  }

  /** §27.8's schedule gate. Returns the reason this dispatch does NOT run, or
   *  `undefined` to let it through.
   *
   *  Everything here is a pure function of (entity id, tick, tier, ops already spent),
   *  and the entity system ticks its mobs in stable id order — so both the cadence
   *  group and the deferred tail are the same on every machine and on every run from
   *  the same state, which is what M45.7 will prove over 10 000 ticks. */
  private schedule(hook: MobHook, bind: MobBinding): ScriptResult | undefined {
    const { tick } = bind;
    // No injected tick source (see the file header): no frame to bound and no phase to
    // stagger by, so the schedule stands aside rather than inventing one.
    if (tick === undefined) return undefined;
    // The tick boundary IS the change in the number the host passed down; there is no
    // second "new frame" call that could be forgotten on one of the two hosts.
    if (tick !== this.frameTick) {
      this.frameTick = tick;
      this.frameOps = 0;
    }
    // Only the periodic hook is scheduled. An event hook fires because something
    // happened to this mob, and deferring one is dropping it: there is no later tick in
    // which a dead mob can still run its `on death`.
    if (hook !== PERIODIC_HOOK) return undefined;
    const divisor = divisorFor(bind.hz);
    if (bind.entityId !== undefined && groupOf(bind.entityId, divisor) !== groupOf(tick, divisor)) {
      return OFF_BEAT;
    }
    // Admit a dispatch only while its WORST case still fits, so the ceiling is a
    // ceiling rather than a line the last dispatch of the tick steps over. The ledger
    // itself accumulates ops actually spent, so a tick of cheap scripts admits more
    // mobs than a tick of expensive ones — deferral tracks real cost, in id order.
    if (this.frameOps + budgetFor('mob', bind.hz) > SCR.MOB.FRAME_CEILING) return OVER_CEILING;
    return undefined;
  }

  /** This type's rung on §27.8's failure ladder, reset whenever the program behind it
   *  is a different one (a renamed script, a changed cadence tier — the edit case goes
   *  through `install`). */
  private typeState(key: string, type: string): TypeState {
    const prev = this.types.get(type);
    if (prev && prev.key === key) return prev;
    const state: TypeState = { key, aborts: 0, quarantined: false };
    this.types.set(type, state);
    return state;
  }

  /** §27.8's failure ladder: abort ⇒ count; `SCR.MOB.QUARANTINE_K` in a row ⇒ the TYPE
   *  is quarantined and ONE chat line is said. A single clean dispatch resets the
   *  count, so "consecutive" means consecutive. */
  private ladder(state: TypeState, ok: boolean, script: string, type: string, host: ScriptHost): void {
    if (ok) {
      state.aborts = 0;
      return;
    }
    if (++state.aborts < SCR.MOB.QUARANTINE_K) return;
    state.quarantined = true;
    // Said exactly once, at the latch: every later dispatch of this type returns at
    // `IS_QUARANTINED` above and never reaches this line again. Which players see it
    // is the host's call (§27.8: creative/ops only, silent in survival) — `say` is the
    // host's verb, and the sim has no game mode to test.
    host.say(quarantineLine(script, type));
  }

  /** The `blockEntities`-phase entry (§27.4 tick placement): sweep every script cell
   *  and dispatch `on redstone` for each newly-powered one, then drain the §27.9
   *  lifecycle queue — "Cascade drain runs once per phase, bounded by §27.10".
   *
   *  The sweep runs FIRST and the drain second, so a lifecycle handler sees the world
   *  this tick's redstone already settled. */
  tick(ctx: ScriptTickCtx): void {
    // The pipeline's own per-tick boundary: §27.10's spawn budget refills here. A host
    // that also injects a tick number refills on that instead (`rollSpawnTick`), which
    // is what keeps a mob-only host — one that never reaches this method — from
    // spending its eight spawns once and never getting them back.
    if (ctx.tick === undefined) this.spawnsUsed = 0;
    else this.rollSpawnTick(ctx.tick);
    for (const cell of ctx.cells) {
      const power = ctx.powerAt(cell.x, cell.y, cell.z);
      const powered = power > 0;
      if (powered && !this.powered.has(cell.key) && ctx.claim?.(cell) !== true) {
        this.install(cell.key, cell.source, 'block');
        this.dispatch(cell.key, { name: REDSTONE_EVENT, power }, {
          host: ctx.host,
          self: { x: cell.x, y: cell.y, z: cell.z, powered },
          tick: ctx.tick,
        });
      }
      if (powered) this.powered.add(cell.key);
      else this.powered.delete(cell.key);
    }
    this.drainCascade(ctx.host, ctx.tick);
    // Signals deliver after the block cascade, so an entity woken by a signal sees the
    // blocks that this tick's lifecycle handlers already moved.
    this.drainSignals(ctx);
  }

  diagnostics(key: string): ScriptDiagnostics {
    const entry = this.cache.get(key);
    // A key with no compiled entry can still have CLIPS: a script whose every lifecycle
    // enqueue was refused never installed anything, and reporting zero there would hide
    // exactly the cascade the author is hunting.
    const clipped = this.clippedFor(key);
    if (!entry) return { ...EMPTY_DIAGNOSTICS, clipped };
    const out: ScriptDiagnostics = {
      installed: entry.program !== null,
      empty: entry.result.ok && entry.result.empty,
      lastOps: entry.lastOps,
      dispatches: entry.dispatches,
      opBudget: entry.opBudget,
      clipped,
      // Copied on the way out: the editor reads diagnostics, it does not get a handle
      // on the runtime's own state. The records themselves are immutable.
      errors: [...entry.errors],
      lastEffects: [...entry.lastEffects],
    };
    if (!entry.result.ok) out.compileError = entry.result.error;
    return out;
  }
}

/** Build the one script runtime for a simulated world. The client's boot and the
 *  server's world host call exactly this — same module, same cache, same latch
 *  (§27.4 "Authority": solo ⇒ the client's sim, multiplayer ⇒ the server's).
 *
 *  `sources` resolves `MobDef.script` names (§27.6); omitted, no mob script resolves
 *  and every mob runs its archetype, which is pre-M45 behaviour and keeps both
 *  existing call sites (`client/boot.ts`, `server/world-host.ts`) compiling unchanged. */
export function createScriptSystem(
  sources: MobScriptSources = NO_SOURCES,
  policy: TierPolicy = SHIPPED_TIER_POLICY,
): ScriptSystem {
  return new Runtime(sources, policy);
}
